import React, { useContext, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import { useABeagleExperiments } from '@buzzfeed/react-components';
import { useUserId } from '@buzzfeed/react-components/lib/hooks/useUserId';
import {
  isOn,
  getExperimentVariant,
} from '@buzzfeed/react-components/lib/utils/abeagle';
import { ABeagleContext, TrackingContext } from '../../contexts';
import abTests from '../../constants/ab-tests';
import { abeagleHost, CLUSTER } from '../../constants';
import { useTrackingContext } from '../../hooks';

const SOURCE = 'island';
const DEFAULT_EXPERIMENT_STATE = {
  eligible: {},
  loaded: false,
  stale: false,
};

const ABeagleProvider = ({ children }) => {
  const router = useRouter();
  const trackingContextValue = useContext(TrackingContext) || {};
  const { trackExperimentActive } = useTrackingContext();
  const userId = useUserId({ cluster: CLUSTER });
  const path = router?.asPath ? router.asPath.split('?')[0] : '';
  const returnedExperiments = useRef({ ...DEFAULT_EXPERIMENT_STATE, path: '' });

  const memoizedArgs = useMemo(
    () => ({
      userId,
      data: { ...trackingContextValue },
    }),
    [userId, JSON.stringify(trackingContextValue)]
  );

  const experiments = useABeagleExperiments({
    abeagleHost:abeagleHost,
    experimentConfig: abTests,
    source: SOURCE,
    ...memoizedArgs,
  });

  if (
    !returnedExperiments.current ||
    returnedExperiments.current.loaded !== experiments.loaded
  ) {
    returnedExperiments.current = {
      ...experiments,
      path,
    };
  }

  returnedExperiments.current.stale = returnedExperiments.current.path !== path;
  const loaded = returnedExperiments.current.loaded;
  const eligibleExperiments = experiments?.eligible || {};
  const experimentKeys = Object.keys(eligibleExperiments).join('|');

  useEffect(() => {
    if (!loaded || !experimentKeys.length || typeof trackExperimentActive !== 'function') {
      return;
    }

    const experimentIds = [];
    Object.keys(eligibleExperiments).forEach((key) => {
      const experiment = eligibleExperiments[key];
      if (experiment && experiment.value) {
        const { id, version, value, variant_id: variantId } = experiment;
        experimentIds.push([key, id, version, value, variantId].join('|'));
      }
    });

    if (experimentIds.length) {
      // Until native app supports ab tests, these CET events will be invalid.  Add back in after native app updates
      // trackExperimentActive({ experiment_id: experimentIds });
    }
  }, [eligibleExperiments, experimentKeys, loaded, trackExperimentActive]);

  const getFeatureFlagValue = (experimentName, onValue = 'on') =>
    isOn(returnedExperiments.current, experimentName, onValue);

  const getExperimentValue = (experimentName, options) =>
    getExperimentVariant(returnedExperiments.current, experimentName, options);

  return (
    <ABeagleContext.Provider
      value={{
        experiments: returnedExperiments.current,
        getFeatureFlagValue,
        getExperimentValue,
      }}
    >
      {children}
    </ABeagleContext.Provider>
  );
};

ABeagleProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
};

ABeagleProvider.defaultProps = {
  children: null,
};

export default ABeagleProvider;
